import React from "react";
import { FormSection, InputField } from "../../../common/FormComponents";
import Tooltip from "../../../common/FormComponents/Tooltip";
import {
  MenuItem,
  Select,
  SelectChangeEvent,
  Switch,
  FormControlLabel,
} from "@mui/material";

type Location = {
  venue: string;
  streetAddress: string;
  city: string;
  province: string;
  postalCode: string;
  country: string;
  isVirtual: boolean;
  virtualEventUrl: string;
};

type LocationSectionProps = {
  location: Location;
  onChange: ({
    field,
    value,
  }: {
    field: keyof Location;
    value: string | boolean;
  }) => void;
  errors?: Record<string, string>;
};

const provinces = [
  { value: "Alberta", label: "Alberta" },
  { value: "British Columbia", label: "British Columbia" },
  { value: "Manitoba", label: "Manitoba" },
  { value: "New Brunswick", label: "New Brunswick" },
  { value: "Newfoundland and Labrador", label: "Newfoundland and Labrador" },
  { value: "Northwest Territories", label: "Northwest Territories" },
  { value: "Nova Scotia", label: "Nova Scotia" },
  { value: "Nunavut", label: "Nunavut" },
  { value: "Ontario", label: "Ontario" },
  { value: "Prince Edward Island", label: "Prince Edward Island" },
  { value: "Quebec", label: "Quebec" },
  { value: "Saskatchewan", label: "Saskatchewan" },
  { value: "Yukon", label: "Yukon" },
];

const countries = [
  { value: "Canada", label: "Canada" },
  { value: "United States", label: "United States" },
  { value: "Other", label: "Other" },
];

const LocationSection = ({
  location,
  onChange,
  errors = {},
}: LocationSectionProps) => {
  const handleChange =
    (field: keyof Omit<Location, "isVirtual">) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ field, value: e.target.value });
    };

  const handleSelectChange =
    (field: keyof Location) => (e: SelectChangeEvent<string>) => {
      onChange({ field, value: e.target.value });
    };

  const handleVirtualToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ field: "isVirtual", value: e.target.checked });
  };

  return (
    <FormSection
      title="Event Location"
      tooltipText="Enter details about where the event will take place"
    >
      <div className="mb-4">
        <FormControlLabel
          control={
            <Switch
              checked={location.isVirtual}
              onChange={handleVirtualToggle}
              color="primary"
            />
          }
          label="This is a virtual event"
        />
      </div>

      {location.isVirtual ? (
        <div className="grid grid-cols-1 md:grid-cols-1 gap-x-6">
          <InputField
            id="virtual-event-url"
            name="virtualEventUrl"
            label="Virtual Event URL"
            value={location.virtualEventUrl}
            onChange={handleChange("virtualEventUrl")}
            error={!!errors.virtualEventUrl}
            helperText={errors.virtualEventUrl || ""}
            required
            tooltipText="Enter the URL where attendees can join the virtual event"
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
          <div className="md:col-span-2">
            <InputField
              id="venue"
              name="venue"
              label="Venue Name"
              value={location.venue}
              onChange={handleChange("venue")}
              error={!!errors.venue}
              helperText={errors.venue || ""}
              required
              tooltipText="Enter the name of the venue where the event will be held"
            />
          </div>

          <div className="md:col-span-2">
            <InputField
              id="street-address"
              name="streetAddress"
              label="Street Address"
              value={location.streetAddress}
              onChange={handleChange("streetAddress")}
              error={!!errors.streetAddress}
              helperText={errors.streetAddress || ""}
              required
              tooltipText="Enter the street address including unit/suite number if applicable"
            />
          </div>

          <InputField
            id="city"
            name="city"
            label="City"
            value={location.city}
            onChange={handleChange("city")}
            error={!!errors.city}
            helperText={errors.city || ""}
            required
            tooltipText="Enter the city name"
          />

          <div className="mb-6">
            <div className="flex items-center mb-1">
              <label
                htmlFor="province"
                className="block text-sm font-medium text-gray-900"
              >
                Province/State
                <span className="text-red-500 ml-1">*</span>
              </label>
              <Tooltip content="Select the province or state" />
            </div>
            <Select
              id="province"
              name="province"
              value={location.province}
              onChange={handleSelectChange("province")}
              displayEmpty
              fullWidth
              error={!!errors.province}
              sx={{
                backgroundColor: "white",
                borderRadius: 3,
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#B0B5BC",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#B0B5BC",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderWidth: 1,
                  borderColor: "#E1A731",
                },
                "&.Mui-error .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#d32f2f",
                },
                "& .MuiSelect-select": {
                  fontSize: 16,
                },
              }}
            >
              <MenuItem value="" disabled>
                Select a province/state
              </MenuItem>
              {provinces.map((province) => (
                <MenuItem key={province.value} value={province.value}>
                  {province.label}
                </MenuItem>
              ))}
            </Select>
            {errors.province && (
              <p className="text-red-500 text-xs mt-1">{errors.province}</p>
            )}
          </div>

          <InputField
            id="postal-code"
            name="postalCode"
            label="Postal/Zip Code"
            value={location.postalCode}
            onChange={handleChange("postalCode")}
            error={!!errors.postalCode}
            helperText={errors.postalCode || ""}
            required
            tooltipText="Enter the postal or zip code"
          />

          <div className="mb-6">
            <div className="flex items-center mb-1">
              <label
                htmlFor="country"
                className="block text-sm font-medium text-gray-900"
              >
                Country
                <span className="text-red-500 ml-1">*</span>
              </label>
              <Tooltip content="Select the country" />
            </div>
            <Select
              id="country"
              name="country"
              value={location.country}
              onChange={handleSelectChange("country")}
              displayEmpty
              fullWidth
              error={!!errors.country}
              sx={{
                backgroundColor: "white",
                borderRadius: 3,
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#B0B5BC",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#B0B5BC",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderWidth: 1,
                  borderColor: "#E1A731",
                },
                "&.Mui-error .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#d32f2f",
                },
                "& .MuiSelect-select": {
                  fontSize: 16,
                },
              }}
            >
              <MenuItem value="" disabled>
                Select a country
              </MenuItem>
              {countries.map((country) => (
                <MenuItem key={country.value} value={country.value}>
                  {country.label}
                </MenuItem>
              ))}
            </Select>
            {errors.country && (
              <p className="text-red-500 text-xs mt-1">{errors.country}</p>
            )}
          </div>
        </div>
      )}
    </FormSection>
  );
};

export default LocationSection;
